import { ProfileFormData } from 'app/components/Profile/ProfileForm/types';
import { Profile } from 'types/profile';
import { LatestSplit, SplitMemberStatus } from 'types/splits.types';
import {
  AddPlaylistAudioContext,
  PlayPlaylistContext,
} from 'types/playlists.types';
import { setShowUpgradePopup } from 'app/store/Storage/slice';

export const getUpdatedIdsUserProperty = (
  profile: Profile,
  formData: Partial<ProfileFormData>
) => {
  const updatedIds: string[] = [];
  const {
    preferredIpi: currentIpi,
    preferredIpn: currentIpn,
    preferredIsni: currentIsni,
  } = profile;
  const { preferredIpi, preferredIpn, preferredIsni } = formData;
  if (!preferredIpi && !preferredIpn && !preferredIsni) {
    return undefined;
  }
  if (currentIpi) {
    updatedIds.push('IPI');
  }
  if (currentIpn) {
    updatedIds.push('IPN');
  }
  if (currentIsni) {
    updatedIds.push('ISNI');
  }
  return updatedIds;
};

export const shouldSendDefaultIdEvent = (
  formData: Partial<ProfileFormData>
) => {
  const { preferredIpi, preferredIpn, preferredIsni } = formData;
  return Boolean(
    preferredIpi?.value || preferredIpn?.value || preferredIsni?.value
  );
};

export const isSplitAcceptedByAll = (split: LatestSplit) =>
  !split.members.find((member) => member.status === SplitMemberStatus.PROPOSED);

const removePersonalDataFromSearchParams = (searchParams: URLSearchParams) => {
  const personalDataKeys = [
    'city',
    'country',
    'email',
    'username',
    'firstName',
    'lastName',
  ];
  personalDataKeys.forEach((key) => searchParams.delete(key));
  return searchParams.toString();
};

const getHashWithoutPersonalData = (hash: string) => {
  const hashParts = hash.split('?');
  if (hashParts.length === 1) {
    return hashParts[0];
  }

  const queryParams = new URLSearchParams(hashParts[1]);
  const newQueryParams = removePersonalDataFromSearchParams(queryParams);
  return newQueryParams ? `${hashParts[0]}?${newQueryParams}` : hashParts[0];
};

export const removePersonalDataFromUrl = (href: string) => {
  const url = new URL(href);

  if (!url.hash) {
    removePersonalDataFromSearchParams(url.searchParams);
    return url.toString();
  }

  const hash = getHashWithoutPersonalData(url.hash);
  return url.origin + url.pathname + hash;
};

export const getAudioAddedLocation = (context?: AddPlaylistAudioContext) => {
  if (context === 'PLAYLIST_ROOM') {
    return 'Playlist detail screen';
  }
  if (context === 'SONG_ROOM') {
    return 'Song Audio tab screen';
  }
  return null;
};

export const getPlaylistPlayedLocation = (context: PlayPlaylistContext) => {
  if (context === 'PLAYLISTS_LIST') {
    return 'Playlist list';
  }
  if (context === 'PLAYLIST_ROOM_TRACK') {
    return 'Playlist detail track list';
  }

  return null;
};

export const getPlaylistUpgradePopupEventName = (
  payload: ReturnType<typeof setShowUpgradePopup>['payload']
) => {
  const { action } = payload?.context || {};

  switch (action) {
    case 'createPlaylist':
      return 'Playlist creation blocked';
    case 'createPlaylistShareLink':
      return 'Playlist share link creation blocked';
    case 'revokePlaylistShareLink':
      return 'Playlist share link revoke blocked';
    case 'setPlaylistShareLinkDownloadable':
      return 'Playlist share link download blocked';
    case 'setPlaylistShareLinkLyrics':
      return 'Playlist share link lyrics blocked';
    case 'setPlaylistShareLinkCredits':
      return 'Playlist share link credits blocked';
    case 'revokeAudioShareLink':
      return 'Audio share link revoke blocked';
    case 'setAudioShareLinkDownloadable':
      return 'Audio share link download blocked';
    case 'setAudioShareLinkLyrics':
      return 'Audio share link lyrics blocked';
    case 'setAudioShareLinkCredits':
      return 'Audio share link credits blocked';
    default:
      return undefined;
  }
};
